import { LockIcon, UnlockIcon } from '@assets/icons';
import Tag from '@components/Tag';
import { consignmentEnumToColor, consignmentEnumToName, ConsignmentStatus } from '@utils/enum-transformers';
import React from 'react';

function statusEquals(status: string | null | undefined, value: ConsignmentStatus): boolean {
    return status === value || status === value.toString();
}

type PillVariant = 'draft' | 'submitted' | 'completed' | 'unlocked';

const iconClassName = 'ConsignmentStatusTag-icon';

/** Fixed width so Draft / Submitted / Completed align in the status column */
const ICON_SLOT_WIDTH_PX = 16;
/** Fits icon + gap + longest uppercase label with horizontal padding (grid(2) each side) */
const STATUS_PILL_WIDTH_PX = 110;

/**
 * Status chip for consignment list/summary (ENVDB-6113 AC1).
 * Pills use a dedicated layout (not {@link Tag}) so we keep 12px / 16px line-height with uppercase label.
 */
const ConsignmentStatusTag: React.FC<{ status: ConsignmentStatus | string | null | undefined }> = ({ status }) => {
    const label = consignmentEnumToName(status as ConsignmentStatus);
    const tagType = consignmentEnumToColor(status as ConsignmentStatus);

    let pillVariant: PillVariant | null = null;
    if (statusEquals(status, ConsignmentStatus.DRAFT)) pillVariant = 'draft';
    else if (statusEquals(status, ConsignmentStatus.SUBMITTED)) pillVariant = 'submitted';
    else if (statusEquals(status, ConsignmentStatus.LOCKED)) pillVariant = 'completed';
    else if (statusEquals(status, ConsignmentStatus.UNLOCKED)) pillVariant = 'unlocked';

    if (pillVariant) {
        return (
            <div
                className="ConsignmentStatusTag-root"
                data-variant={pillVariant}
            >
                <style jsx>{`
                    @use 'sass:color';
                    @use 'vars' as *;
                    @use 'utils' as *;

                    .ConsignmentStatusTag-pill {
                        box-sizing: border-box;
                        display: inline-flex;
                        flex-direction: row;
                        align-items: center;
                        justify-content: flex-start;
                        gap: grid(1);
                        /* Match Tag + design tokens: _vars.scss $border-radius, grid padding */
                        padding: grid(1) grid(2);
                        min-height: grid(6);
                        min-width: ${STATUS_PILL_WIDTH_PX}px;
                        border-radius: $border-radius;
                        border-style: solid;
                        border-width: 1px;
                        font-size: 12px;
                        font-weight: 500;
                        line-height: 16px;
                        letter-spacing: 0.12px;
                        text-transform: uppercase;
                        white-space: nowrap;
                    }

                    .ConsignmentStatusTag-label {
                        display: inline-block;
                        line-height: 16px;
                        min-width: 0;
                        flex: 1;
                        text-align: center;
                    }

                    /* Same width for every row: icon area is fixed; Draft uses empty slot */
                    .ConsignmentStatusTag-iconSlot {
                        display: inline-flex;
                        flex-shrink: 0;
                        align-items: center;
                        justify-content: center;
                        width: ${ICON_SLOT_WIDTH_PX}px;
                        min-width: ${ICON_SLOT_WIDTH_PX}px;
                        height: ${ICON_SLOT_WIDTH_PX}px;
                    }

                    .ConsignmentStatusTag-iconSlot :global(.ConsignmentStatusTag-icon) {
                        display: block;
                        width: ${ICON_SLOT_WIDTH_PX}px;
                        height: ${ICON_SLOT_WIDTH_PX}px;
                        min-width: ${ICON_SLOT_WIDTH_PX}px;
                        min-height: ${ICON_SLOT_WIDTH_PX}px;
                        flex-shrink: 0;
                    }

                    .ConsignmentStatusTag-root[data-variant='draft'] .ConsignmentStatusTag-pill {
                        background: transparent;
                        color: $color-grey47;
                        border-color: $grey-border;
                    }

                    .ConsignmentStatusTag-root[data-variant='submitted'] .ConsignmentStatusTag-pill {
                        background: transparent;
                        color: $color-secondary;
                        border-color: rgba($color-secondary, 0.35);
                    }

                    .ConsignmentStatusTag-root[data-variant='completed'] .ConsignmentStatusTag-pill {
                        background: transparent;
                        color: $color-primary;
                        border-color: rgba($color-primary, 0.45);
                    }

                    .ConsignmentStatusTag-root[data-variant='unlocked'] .ConsignmentStatusTag-pill {
                        background: transparent;
                        color: $color-secondary;
                        border-color: rgba($color-secondary, 0.35);
                    }

                    @media (prefers-color-scheme: dark) {
                        .ConsignmentStatusTag-root[data-variant='draft'] .ConsignmentStatusTag-pill {
                            background: transparent;
                            color: $color-subtitle;
                            border-color: rgba($color-border, 0.5);
                        }

                        .ConsignmentStatusTag-root[data-variant='submitted'] .ConsignmentStatusTag-pill {
                            background: transparent;
                            color: color.adjust($color-secondary, $lightness: 35%);
                            border-color: rgba($color-secondary, 0.45);
                        }

                        .ConsignmentStatusTag-root[data-variant='completed'] .ConsignmentStatusTag-pill {
                            background: transparent;
                            color: color.adjust($color-primary, $lightness: 40%);
                            border-color: rgba($color-primary, 0.55);
                        }

                        .ConsignmentStatusTag-root[data-variant='unlocked'] .ConsignmentStatusTag-pill {
                            background: transparent;
                            color: color.adjust($color-secondary, $lightness: 35%);
                            border-color: rgba($color-secondary, 0.45);
                        }
                    }
                `}</style>
                <div className="ConsignmentStatusTag-pill">
                    {['submitted', 'completed', 'unlocked'].includes(pillVariant) && (
                        <span
                            className="ConsignmentStatusTag-iconSlot"
                            aria-hidden
                        >
                            {pillVariant === 'submitted' || pillVariant === 'unlocked' ? (
                                <UnlockIcon
                                    className={iconClassName}
                                    aria-hidden
                                />
                            ) : pillVariant === 'completed' ? (
                                <LockIcon
                                    className={iconClassName}
                                    aria-hidden
                                />
                            ) : null}
                        </span>
                    )}
                    <span className="ConsignmentStatusTag-label">{label}</span>
                </div>
            </div>
        );
    }

    return (
        <Tag
            tagType={tagType}
            text={label}
        />
    );
};

export default ConsignmentStatusTag;
